import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { ListBulletsIcon } from "@phosphor-icons/react";

// Live play-by-play feed. Real MLB plays when available, deterministic for other sports.
export default function PlayByPlay({ gameId, gameStatus }) {
  const [data, setData] = useState({ plays: [], is_real: false, status: gameStatus });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/games/${gameId}/plays`);
      setData(r.data);
    } catch (err) {
      captureError("plays load failed:", err);
    }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (gameStatus !== "LIVE") return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [gameStatus, load]);

  return (
    <div className="p-4 rounded-md bg-[#121212] border border-white/10" data-testid="play-by-play">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ListBulletsIcon weight="bold" size={16} className="text-[#0A84FF]" />
          <h3 className="font-display font-bold text-lg uppercase tracking-tighter">Play-by-Play</h3>
        </div>
        {data.is_real && (
          <span className="text-[9px] font-black uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40">Live data</span>
        )}
      </div>

      {data.status === "UPCOMING" ? (
        <p className="text-xs text-white/45" data-testid="pbp-upcoming">Play-by-play starts at first pitch / tip-off.</p>
      ) : data.plays.length === 0 ? (
        <p className="text-xs text-white/45" data-testid="pbp-empty">No plays available yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1" data-testid="pbp-list">
          {data.plays.map((p) => (
            <div
              key={p.id}
              data-testid={`pbp-${p.id}`}
              className={`flex gap-2 px-2.5 py-2 rounded ${p.is_scoring ? "bg-[#32D74B]/10 border border-[#32D74B]/30" : "bg-white/[0.02]"}`}
            >
              <span className="font-num text-[10px] font-black text-white/40 shrink-0 mt-0.5 w-12 text-right">{p.period}</span>
              <p className={`text-xs leading-snug ${p.is_scoring ? "text-[#32D74B] font-semibold" : "text-white/75"}`}>{p.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
